import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import moment from 'moment';
import { getThreadChatMessages, readThreadChatMessage } from 'flow/actions/chat-actions';
import { MessageThreadCheckProvider } from 'flow/contexts/MessageThreadCheckProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useRovingMessageList from 'flow/hooks/useRovingMessageList';
import MessageGroup from 'flow/pages/messages/MessageGroup';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ThreadMessageList = function ThreadMessageList(_ref) {
  var _groupMessages;
  var messageKey = _ref.messageKey,
    sessionKey = _ref.sessionKey,
    register = _ref.register;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var threadListContainerRef = useRef(null);
  var rovingHandlers = useRovingMessageList(threadListContainerRef);
  var threadEndPlaceHolderRef = useRef(null);
  var lastFetchedThreadMessageKey = useRef(null);
  var isSelfRef = useRef(false);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    currentScrollHeight = _useState2[0],
    setCurrentScrollHeight = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    currentScrollTop = _useState4[0],
    setCurrentScrollTop = _useState4[1];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    isBottom = _useState6[0],
    setIsBottom = _useState6[1];
  var dispatch = useAppDispatch();
  var _useExtension = useExtension(),
    ownExtension = _useExtension.ownExtension;
  var imageLoaded = useAppSelector(function (state) {
    return state.chats.get('attachmentImageLoading');
  });
  var threadMessages = useAppSelector(function (state) {
    return state.chats.getIn(['threadMessage', 'threadMessages', sessionKey, messageKey]);
  });
  useEffect(function () {
    register({
      updateMessageSend: updateMessageSend
    });
  }, [register]);
  var isAtBottom = function isAtBottom(containerRef) {
    if (!containerRef.current) {
      return false;
    }
    var _containerRef$current = containerRef.current,
      scrollTop = _containerRef$current.scrollTop,
      clientHeight = _containerRef$current.clientHeight,
      scrollHeight = _containerRef$current.scrollHeight;
    var scrollClientHeight = Math.abs(scrollTop + clientHeight) + 50;
    return scrollClientHeight > scrollHeight;
  };
  var updateMessageSend = function updateMessageSend() {
    return threadScrollUntilBottom(true);
  };
  var threadScrollUntilBottom = useCallback(function () {
    var isSelf = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var threadElement = threadListContainerRef.current;
    isSelfRef.current = isSelf;
    if (threadElement) {
      var scrollHeight = threadElement.scrollHeight,
        scrollTop = threadElement.scrollTop,
        clientHeight = threadElement.clientHeight;
      var isCurrentlyBottom = scrollHeight === scrollTop + clientHeight && scrollTop > 0;
      var hasScroll = scrollHeight > clientHeight;
      if (isCurrentlyBottom) {
        isSelfRef.current = false;
      }
      if ((!isCurrentlyBottom && isBottom || isSelfRef.current) && hasScroll) {
        threadElement.scrollTop += 100;
        requestAnimationFrame(function () {
          return threadScrollUntilBottom(isSelf);
        });
      }
    }
  }, [threadListContainerRef, isBottom]);
  var isOwnLastMessage = useMemo(function () {
    var _threadMessages$last;
    return ownExtension.get('key') === (threadMessages === null || threadMessages === void 0 || (_threadMessages$last = threadMessages.last()) === null || _threadMessages$last === void 0 ? void 0 : _threadMessages$last.getIn(['from', 'extensionKey']));
  }, [ownExtension, threadMessages]);
  useEffect(function () {
    var lastReadMessage = threadMessages === null || threadMessages === void 0 ? void 0 : threadMessages.last();
    if (lastReadMessage !== null && lastReadMessage !== void 0 && lastReadMessage.get('key') && !lastReadMessage.get('read')) {
      dispatch(readThreadChatMessage(sessionKey, messageKey, lastReadMessage.get('key')));
    }
    if (lastReadMessage !== null && lastReadMessage !== void 0 && lastReadMessage.get('key') && lastReadMessage.get('key') !== lastFetchedThreadMessageKey.current) {
      lastFetchedThreadMessageKey.current = lastReadMessage.get('key');
      dispatch(getThreadChatMessages(sessionKey, messageKey, null)).then(updateScrollerHeight);
    }
  }, [dispatch, messageKey, sessionKey, threadMessages]);
  useEffect(function () {
    var _threadListContainerR;
    if (isAtBottom(threadListContainerRef) || isOwnLastMessage) {
      threadScrollUntilBottom(false);
    } else if (!isAtBottom(threadListContainerRef) && currentScrollHeight <= ((_threadListContainerR = threadListContainerRef.current) === null || _threadListContainerR === void 0 ? void 0 : _threadListContainerR.scrollHeight)) {
      var _threadListContainerR2;
      threadListContainerRef === null || threadListContainerRef === void 0 || threadListContainerRef.current.scrollTo({
        top: ((_threadListContainerR2 = threadListContainerRef.current) === null || _threadListContainerR2 === void 0 ? void 0 : _threadListContainerR2.scrollHeight) - currentScrollHeight + currentScrollTop,
        behavior: 'instant'
      });
    }
  }, [threadMessages, messageKey, imageLoaded, isOwnLastMessage]);
  var fetchOldThreadMessages = useCallback(function (beforeKey) {
    dispatch(getThreadChatMessages(sessionKey, messageKey, beforeKey)).then(updateScrollerHeight);
  }, [dispatch, messageKey, sessionKey]);
  var updateScrollerHeight = function updateScrollerHeight() {
    var _threadListContainerR3, _threadListContainerR4;
    setCurrentScrollHeight((_threadListContainerR3 = threadListContainerRef.current) === null || _threadListContainerR3 === void 0 ? void 0 : _threadListContainerR3.scrollHeight);
    setCurrentScrollTop((_threadListContainerR4 = threadListContainerRef.current) === null || _threadListContainerR4 === void 0 ? void 0 : _threadListContainerR4.scrollTop);
  };
  var groupMessages = function groupMessages(messages) {
    var display = [];
    var currentDateGroup;
    messages === null || messages === void 0 || messages.forEach(function (message) {
      var _currentDateGroup;
      var date = moment(message === null || message === void 0 ? void 0 : message.get('sent')).format('YYYY-MM-DD');
      if (((_currentDateGroup = currentDateGroup) === null || _currentDateGroup === void 0 ? void 0 : _currentDateGroup.date) !== date) {
        currentDateGroup = {
          date: date,
          messages: []
        };
        display.push(currentDateGroup);
      }
      currentDateGroup.messages.push(message);
    });
    return display;
  };
  var scrollThreadMessages = useCallback(function () {
    var scrollTop = threadListContainerRef.current.scrollTop;
    if (scrollTop <= 50) {
      var _threadMessages$first;
      setIsBottom(false);
      fetchOldThreadMessages(threadMessages === null || threadMessages === void 0 || (_threadMessages$first = threadMessages.first()) === null || _threadMessages$first === void 0 ? void 0 : _threadMessages$first.get('key'));
    }
  }, [fetchOldThreadMessages, threadListContainerRef, threadMessages]);
  useEffect(function () {
    if (threadListContainerRef.current) {
      threadListContainerRef.current.addEventListener('scrollend', scrollThreadMessages);
    }
    return function () {
      var _threadListContainerR5;
      threadListContainerRef === null || threadListContainerRef === void 0 || (_threadListContainerR5 = threadListContainerRef.current) === null || _threadListContainerR5 === void 0 || _threadListContainerR5.removeEventListener('scrollend', scrollThreadMessages);
    };
  }, [threadMessages, threadListContainerRef]);
  useEffect(function () {
    setCurrentScrollHeight(0);
  }, [messageKey, sessionKey]);
  useEffect(function () {
    setIsBottom(true);
  }, [threadMessages, messageKey, sessionKey]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
    ref: threadListContainerRef,
    role: "feed",
    "aria-label": i18next.t('ThreadMessages.DefaultTitle')
  }, rovingHandlers), {}, {
    className: "scrollbar h-fit flex-1 items-end overflow-x-hidden p-3",
    children: [/*#__PURE__*/_jsx(MessageThreadCheckProvider, {
      value: true,
      children: (_groupMessages = groupMessages(threadMessages)) === null || _groupMessages === void 0 ? void 0 : _groupMessages.map(function (group) {
        return /*#__PURE__*/_jsx(MessageGroup, {
          group: group,
          sessionKey: sessionKey,
          showContextMenu: false
        }, group.date);
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "bottom-0 block h-3",
      ref: threadEndPlaceHolderRef
    })]
  }));
};
export default ThreadMessageList;